import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { 
  Brain,
  Send,
  Loader2,
  MessageSquare,
  Sparkles,
  Bot
} from 'lucide-react';
import { GoogleGenerativeAI } from "@google/generative-ai";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  isLoading?: boolean;
}

interface AICareerAdvisorProps {
  currentRole?: string;
  targetRole?: string;
  profile?: any;
  jobPreferences?: {
    cultureFit?: number;
    compensation?: number;
    workLifeBalance?: number;
    commuteLocation?: number;
    roleFulfillment?: number;
    stability?: number;
    skillsDevelopment?: number;
    industryOutlook?: number;
    teamLeadership?: number;
    growthPotential?: number;
    highImpactCategories?: string[];
  };
  onSuggestion?: (suggestion: any) => void;
}

export default function AICareerAdvisor({ 
  currentRole, 
  targetRole,
  profile,
  jobPreferences,
  onSuggestion 
}: AICareerAdvisorProps) {
  const { toast } = useToast();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentConversation, setCurrentConversation] = useState<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [currentResponse, setCurrentResponse] = useState('');
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [shouldScroll, setShouldScroll] = useState(true);

  useEffect(() => {
    if (profile) {
      initializeConversation();
    }
  }, [profile]);

  const getCurrentRole = () => {
    if (!profile?.experiences?.length) return 'Not specified';
    
    // Find current experience
    const currentExp = profile.experiences.find((exp: any) => exp.current === true);
    if (currentExp) {
      return `${currentExp.position} at ${currentExp.company?.name || currentExp.company_name}`;
    }
    
    // If no current experience, use most recent
    const sortedExperiences = [...profile.experiences].sort((a, b) => 
      new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
    );
    
    if (sortedExperiences[0]) {
      return `${sortedExperiences[0].position} at ${sortedExperiences[0].company?.name || sortedExperiences[0].company_name}`;
    }

    return 'Not specified';
  };

  const initializeConversation = async () => {
    try {
      const detectedRole = getCurrentRole();

      // Create a new conversation
      const { data: conversation, error } = await supabase
        .from('career_conversations')
        .insert({
          profile_id: profile.id,
          title: 'Career Planning Session',
          context: {
            currentRole: detectedRole,
            targetRole,
            jobPreferences,
            profile: {
              experiences: profile.experiences?.length || 0,
              education: profile.education?.length || 0
            }
          }
        })
        .select()
        .single();

      if (error) throw error;
      setCurrentConversation(conversation);

      // Set initial message
      let initialContext = `Based on your profile:
- Current Role: ${detectedRole}
- Experience: ${profile.experiences?.length || 0} positions
- Education: ${profile.education?.length || 0} degrees\n`;

      if (jobPreferences) {
        initialContext += `\nYour current job preferences indicate:\n`;
        if (jobPreferences.highImpactCategories?.length) {
          initialContext += `High priority areas:\n`;
          jobPreferences.highImpactCategories.forEach(category => {
            initialContext += `- ${category}\n`;
          });
        }
      }

      initialContext += `\nI can help with career paths, and professional development based on your preferences and goals.`;

      setMessages([{
        role: 'assistant',
        content: initialContext
      }]);

    } catch (error) {
      console.error('Error initializing conversation:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to start career planning session'
      });
    }
  };

  const saveConversationUpdate = async (newMessage: Message, suggestions?: any) => {
    if (!currentConversation) return;

    try {
      const { error } = await supabase
        .from('career_conversations')
        .update({
          updated_at: new Date().toISOString(),
          suggestions: suggestions || currentConversation.suggestions
        })
        .eq('id', currentConversation.id);

      if (error) throw error;

      // If we have career path suggestions, save them
      if (suggestions?.careerPaths) {
        const pathsToSave = suggestions.careerPaths.map((path: any) => ({
          profile_id: profile.id,
          conversation_id: currentConversation.id,
          title: path.title,
          description: path.description,
          current_role: currentRole,
          target_role: path.title,
          match_score: path.matchScore,
          requirements: path.requirements || [],
          milestones: path.milestones || [],
          skills_required: path.requiredSkills || []
        }));

        const { error: pathError } = await supabase
          .from('career_paths')
          .insert(pathsToSave);

        if (pathError) throw pathError;
      }

    } catch (error) {
      console.error('Error saving conversation update:', error);
    }
  };

  const scrollToBottom = () => {
    if (shouldScroll && chatContainerRef.current) {
      const { scrollHeight, clientHeight } = chatContainerRef.current;
      chatContainerRef.current.scrollTop = scrollHeight - clientHeight;
    }
  };

  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
    setShouldScroll(isAtBottom);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, currentResponse]);

  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (chatContainer) {
      chatContainer.addEventListener('scroll', handleScroll);
      return () => chatContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const simulateTyping = async (text: string) => {
    setIsTyping(true);
    let currentText = '';
    
    const words = text.split(' ');
    
    for (let i = 0; i < words.length; i++) {
      currentText += (i > 0 ? ' ' : '') + words[i];
      setCurrentResponse(currentText);
      await new Promise(resolve => setTimeout(resolve, Math.random() * 40 + 30));
    }
    
    setIsTyping(false);
    setCurrentResponse('');
    return text;
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    if (!import.meta.env.VITE_GEMINI_API_KEY) {
      console.error('Gemini API key not configured');
      return;
    }

    setIsLoading(true);
    setShouldScroll(true);
    
    try {
      const userMessage = { role: 'user' as const, content: input };
      setMessages(prev => [...prev, userMessage]);
      setInput('');

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '', 
        isLoading: true 
      }]);

      const genAI = new GoogleGenerativeAI(import.meta.env.VITE_GEMINI_API_KEY);
      const model = genAI.getGenerativeModel({ model: "models/gemini-2.0-flash" });

      const context = {
        profile: {
          currentRole,
          targetRole,
          experiences: profile?.experiences?.map((e: any) => ({
            position: e.position,
            company: e.company_name,
            description: e.description
          })),
          education: profile?.education?.map((e: any) => ({
            degree: e.degree,
            field: e.field_of_study,
            institution: e.institution
          }))
        },
        jobPreferences: jobPreferences ? {
          priorities: jobPreferences.highImpactCategories || [],
          scores: {
            cultureFit: jobPreferences.cultureFit,
            compensation: jobPreferences.compensation,
            workLifeBalance: jobPreferences.workLifeBalance,
            commuteLocation: jobPreferences.commuteLocation,
            roleFulfillment: jobPreferences.roleFulfillment,
            stability: jobPreferences.stability,
            skillsDevelopment: jobPreferences.skillsDevelopment,
            industryOutlook: jobPreferences.industryOutlook,
            teamLeadership: jobPreferences.teamLeadership,
            growthPotential: jobPreferences.growthPotential
          }
        } : null,
        previousMessages: messages.map(m => `${m.role}: ${m.content}`).join('\n'),
        userQuery: input
      };

      const prompt = `
        You are an AI career advisor with access to this user's professional profile and job preferences:
        ${JSON.stringify(context, null, 2)}
        
        Provide specific, actionable advice based on their background, goals, and current job preferences.
        Focus on:
        - Concrete next steps
        - Industry insights
        - Professional growth opportunities
        
        If job preferences indicate urgency in certain areas, prioritize addressing those concerns.
        Keep responses clear, concise, and directly relevant to their profile. Try to keep it conversational and brief.
      `;

      const result = await model.generateContent(prompt);
      const response = await result.response;
      const text = response.text();

      setMessages(prev => prev.filter(m => !m.isLoading));

      await simulateTyping(text);

      const newMessage = { 
        role: 'assistant' as const, 
        content: text 
      };

      setMessages(prev => [...prev, newMessage]);
      
      // Save conversation update
      await saveConversationUpdate(newMessage);

      if (onSuggestion) {
        try {
          const skillsMatch = text.match(/recommended skills?:?\s*([^.]+)/i);
          const pathsMatch = text.match(/career paths?:?\s*([^.]+)/i);
          const stepsMatch = text.match(/next steps?:?\s*([^.]+)/i);

          const suggestions = {
            skills: skillsMatch?.[1]?.split(',').map(s => s.trim()) || [],
            paths: pathsMatch?.[1]?.split(',').map(s => s.trim()) || [],
            nextSteps: stepsMatch?.[1]?.split(',').map(s => s.trim()) || []
          };

          onSuggestion(suggestions);
        } catch (error) {
          console.error('Error parsing suggestions:', error);
        }
      }

    } catch (error) {
      console.error('Error getting AI response:', error);
      setMessages(prev => prev.filter(m => !m.isLoading));
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'I apologize, but I encountered an error. Please try again.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="border-primary/10">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Brain className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle>AI Career Advisor</CardTitle>
              <CardDescription>Get personalized career guidance and advice</CardDescription>
            </div>
          </div>
          <Badge variant="outline" className="gap-1">
            <Sparkles className="h-3 w-3" />
            AI Powered
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div 
          ref={chatContainerRef}
          className="space-y-4 min-h-[300px] max-h-[500px] overflow-y-auto p-4 rounded-lg bg-muted/50 border"
          onScroll={handleScroll}
        >
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bot className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-medium mb-2">Ask me anything about your career!</h3>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                I can help with career paths, and professional development strategies.
              </p>
            </div>
          ) : (
            <>
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex gap-3 ${
                    message.role === 'assistant' ? 'flex-row' : 'flex-row-reverse'
                  }`}
                >
                  {message.role === 'assistant' ? (
                    <Avatar className="h-8 w-8">
                      <AvatarImage src="/ai-avatar.png" />
                      <AvatarFallback className="bg-primary/10">
                        <Brain className="h-4 w-4 text-primary" />
                      </AvatarFallback>
                    </Avatar>
                  ) : (
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={profile?.avatar_url} />
                      <AvatarFallback>
                        {profile?.full_name?.[0] || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  )}
                  
                  <div
                    className={`rounded-lg p-4 max-w-[80%] ${
                      message.role === 'assistant'
                        ? 'bg-background border shadow-sm'
                        : 'bg-primary text-primary-foreground'
                    }`}
                  >
                    {message.isLoading ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Thinking...</span>
                      </div>
                    ) : (
                      <div className={`prose prose-sm max-w-none ${
                        message.role === 'assistant' 
                          ? 'dark:prose-invert text-foreground' 
                          : 'prose-invert'
                      }`}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {message.content}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {isTyping && currentResponse && (
                <div className="flex gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src="/ai-avatar.png" />
                    <AvatarFallback className="bg-primary/10">
                      <Brain className="h-4 w-4 text-primary" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="rounded-lg p-4 max-w-[80%] bg-background border shadow-sm">
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {currentResponse}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="flex gap-2 pt-2">
          <Textarea
            placeholder="Ask about career paths or professional development..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            className="min-h-[60px] bg-background resize-none"
          />
          <Button 
            onClick={sendMessage} 
            disabled={isLoading || !input.trim()}
            className="flex-shrink-0"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export { AICareerAdvisor }